import { useMemo, useState } from "react";
import { PlusCircleIcon } from "../../Icons";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { number } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { invoiceFormSchema } from "../../../schemas/CreateInvoiceType";
import InputWithLabel from "../../InputWithLabel";

type InvoiceForm = {
  billFrom: string;
  billTo: string;
  invoiceNo: string;
  date: string;
  paymentTerms: string;
  dueDate: string;
  poNumber: string;
  notes: string;
  terms: string;
  bank: string;
  tax: string;
  shipping: string;
  discount: string;
  amountPaid: number;
  balanceDue: string;
  items: {
    description: string;
    quantity: number;
    rate: number;
  }[];
};

const bankList = [
  { label: "State Bank of India", value: "STATE_BANK_OF_INDIA" },
  { label: "HDFC Bank", value: "HDFC_BANK" },
  { label: "ICICI Bank", value: "ICICI_BANK" },
  { label: "Axis Bank", value: "AXIS_BANK" },
  { label: "Punjab National Bank", value: "PUNJAB_NATIONAL_BANK" },
  { label: "Bank of Baroda", value: "BANK_OF_BARODA" },
  { label: "Canara Bank", value: "CANARA_BANK" },
  { label: "Union Bank of India", value: "UNION_BANK_OF_INDIA" },
  { label: "Bank of India", value: "BANK_OF_INDIA" },
  { label: "Indian Bank", value: "INDIAN_BANK" },
  { label: "Central Bank of India", value: "CENTRAL_BANK_OF_INDIA" },
  { label: "Kotak Mahindra Bank", value: "KOTAK_MAHINDRA_BANK" },
  { label: "IndusInd Bank", value: "INDUSIND_BANK" },
  { label: "Yes Bank", value: "YES_BANK" },
  { label: "IDFC First Bank", value: "IDFC_FIRST_BANK" },
  { label: "Federal Bank", value: "FEDERAL_BANK" },
  { label: "RBL Bank", value: "RBL_BANK" },
  { label: "South Indian Bank", value: "SOUTH_INDIAN_BANK" },
  { label: "UCO Bank", value: "UCO_BANK" },
  { label: "IDBI Bank", value: "IDBI_BANK" },
];

const initialState = { description: "", quantity: 0, rate: 0 };
export const CreateInvoice = ({ toggleDialog }: { toggleDialog: any }) => {
  const [submittedData, setSubmittedData] = useState<string>("");

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<InvoiceForm>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: {
      items: [initialState],
      discount: "",
      tax: "",
      shipping: "",
      amountPaid: 0,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const items = useWatch({ control, name: "items" });
  const discount = useWatch({ control, name: "discount" });
  const tax = useWatch({ control, name: "tax" });
  const shipping = useWatch({ control, name: "shipping" });
  const amountPaid = useWatch({ control, name: "amountPaid" });

  const subtotal = useMemo(() => {
    return (
      items?.reduce(
        (sum, item) =>
          sum + (Number(item.quantity) || 0) * (Number(item.rate) || 0),
        0
      ) || 0
    );
  }, [items]);

  // const discountAmount = subtotal * (Number(discount) / 100);
  // const taxableAmount = subtotal -discountAmount
  // const taxAmount = taxableAmount * (Number(tax)/100)

  // const total = taxableAmount + taxAmount + shipping;
  // const balanceDue = Number(total) - amountPaid;

  const discountAmount = useMemo(() => {
    return subtotal * ((Number(discount) || 0) / 100);
  }, [subtotal, discount]);

  const taxableAmount = subtotal - discountAmount;

  const taxAmount = useMemo(() => {
    return taxableAmount * ((Number(tax) || 0) / 100);
  }, [taxableAmount, tax]);

  const total = useMemo(() => {
    return taxableAmount + taxAmount + (Number(shipping) || 0);
  }, [taxableAmount, taxAmount, shipping]);

  const balanceDue = useMemo(() => {
    return total - (Number(amountPaid) || 0);
  }, [total, amountPaid]);

  const onSubmit = (data: InvoiceForm) => {
    setSubmittedData(
      JSON.stringify({ ...data, subtotal, total, balanceDue }, null, 2)
    );
    console.log(JSON.stringify(data));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="min-h-screen text-white p-8">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Top Section */}
          <div className="flex justify-between items-start gap-6">
            <div className="w-[500px] flex items-start gap-4">
              <div>
                <label className="text-black">Bill Form</label>
                <select
                  {...register("billFrom")}
                  className="w-full rounded-md px-4 py-3 text-black"
                >
                  <option value="Taram">Select Taram Entity</option>
                </select>
                {errors.billFrom && (
                  <p className="text-red-500 text-xs">
                    {errors.billFrom.message}
                  </p>
                )}
              </div>

              <div>
                <label className="text-black">Bill To</label>
                <input
                  {...register("billTo")}
                  placeholder="Who is this to?"
                  className="w-full rounded-md px-4 py-3 text-black"
                />
                {errors.billTo && (
                  <p className="text-red-500 py-2 text-xs">
                    {errors.billTo.message}
                  </p>
                )}
              </div>
            </div>

            <div className="gap-4">
              {[
                { label: "#", name: "invoiceNo",type:"number" },
                { label: "Date", name: "date",type:"date" },
                { label: "Payment Terms", name: "paymentTerms" },
                { label: "Due Date", name: "dueDate",type:"date" },
                { label: "PO Number", name: "poNumber" },
              ].map((field) => {
                const error = errors[field.name as keyof InvoiceForm];

                return (
                  <div
                    key={field.name}
                    className="flex flex-col items-end my-2"
                  >
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-400">
                        {field.label}
                      </label>

                      <input
                        type={field?.type|| ""}
                        {...register(field.name as keyof InvoiceForm)}
                        className={`w-[200px] border rounded-md px-3 py-2 text-black
              ${error ? "border-red-500" : "border-custom-gray"}
            `}
                      />
                    </div>

                    {error && (
                      <p className="text-red-500 text-xs mt-1 w-[200px] text-left">
                        {error.message as string}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Items Table */}
          <div className="rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 bg-gray-600 text-sm px-4 py-2">
              <div className="col-span-6">Item</div>
              <div className="col-span-2 text-center mr-12">Quantity</div>
              <div className="col-span-2 text-center mr-12">Rate</div>
              <div className="col-span-2 text-right mr-12">Amount</div>
            </div>

            {fields.map((field, index) => {
              const qty = items?.[index]?.quantity || 0;
              const rate = items?.[index]?.rate || 0;
              const amount = qty * rate;

              return (
                <div key={field.id} className="flex items-center">
                  <div className="grid grid-cols-[420px_140px_130px_100px_10px] gap-2 py-2 w-full">
                    <InputWithLabel label={""} 
                    {...register(`items.${index}.description`, {
                          required: "Description is required",
                        })}
                        error={errors?.items?.[index]?.description?.message}
                        placeholder="Description of items/goods"
                        className="!w-full !col-span-3"
                        aria-multiline="true"
                    />
                      <InputWithLabel
                        label=""
                        type="number"
                        {...register(`items.${index}.quantity`, {
                          required: true,
                          valueAsNumber:true
                        })}
                        className="!col-span-2 rounded-md px-3 py-2 border-2 !border-slate-400 text-black text-center"
                        placeholder="Enter Quantity"
                        error={errors.items?.[index]?.quantity?.message}
                      />
                      <InputWithLabel
                        label=""
                        type="number"
                        error=  {errors.items?.[index]?.rate?.message}
                        {...register(`items.${index}.rate`, {
                          required: true,
                          valueAsNumber:true
                        
                        })}
                        className="text-center"
                        placeholder="$ 0"
                      />
                    <div className="col-span-2 mr-[10px] flex items-center justify-end text-gray-300">
                      ${amount.toFixed(2)}
                    </div>
                  </div>
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="text-white bg-red-500 rounded-full w-8 h-8 mx-1"
                    >
                      ✕
                    </button>
                  )}

                  {index === 0 && (
                    <button type="button" onClick={() => append(initialState)}>
                      <PlusCircleIcon className="size-10 text-custom-blue" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Notes & Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <textarea
                {...register("notes")}
                placeholder="Any relevant information to the payee"
                className="w-full rounded-md px-4 py-3 text-black"
              />

              <textarea
                {...register("terms")}
                placeholder="Terms & conditions - late fees, payment methods"
                className="w-full rounded-md px-4 py-3 text-black"
              />

              <select
                {...register("bank")}
                className="w-full rounded-md px-4 py-3 text-black"
              >
                {bankList.map((el) => (
                  <option value={el.value}>{el.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-3 text-sm text-black">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Discount (%)</span>
                <input
                  type="number"
                  {...register("discount", {
                    required: true,
                    min: 0,
                    valueAsNumber: true,
                  })}
                  className="w-24 rounded-md px-2 text-center py-1 text-black"
                />
                  {errors.discount && (
                  <p className="text-red-500 text-xs">
                    {errors.discount.message}
                  </p>
                )}
              </div>

              <div className="flex justify-between items-center">
                <span>Tax (%)</span>
                <input
                  type="number"
                  {...register("tax", {
                    required: true,
                    min: 0,
                    valueAsNumber: true,
                  })}
                  className="w-24 rounded-md px-2 py-1 text-center text-black"
                />
                  {errors.tax && (
                  <p className="text-red-500 text-xs">
                    {errors.tax.message}
                  </p>
                )}
              </div>

              <div className="flex justify-between items-center">
                <span>Shipping</span>
                <input
                  type="number"
                  {...register("shipping", {
                    required: true,
                    min: 0,
                    valueAsNumber: true,
                  })}
                  className="w-24 rounded-md px-2 py-1 text-center text-black"
                />
                  {errors.shipping && (
                  <p className="text-red-500 text-xs">
                    {errors.shipping.message}
                  </p>
                )}
              </div>

              <div className="flex justify-between pt-2 border-t border-gray-700">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center">
                <span>Amount Paid</span>
                <input
                  type="number"
                  
                  {...register("amountPaid", { required: true, min: 0,valueAsNumber:true })}
                  className="w-24 rounded-md px-2 py-1 text-right text-black"
                />
                {errors.amountPaid && (
                  <p className="text-red-500 text-xs">
                    {errors.amountPaid.message}
                  </p>
                )}
              </div>

              <div className="flex justify-between text-red-700">
                <span className="">Balance Due</span>
                <span>${balanceDue.toFixed(2)}</span>
              </div>

              {/* <div className="flex justify-between pt-2 border-t border-gray-700">
                <span>Total</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center">
                <label>BalcanceDue</label>
                <input
                  type="number"
                  {...register("balanceDue")}
                  className="w-24 rounded-md px-2 py-1 text-black"
                />
              </div> */}
            </div>
          </div>
        </div>

        <div className="flex float-right my-10 py-10">
          <button type="submit" className="bg-custom-blue px-5 py-2 rounded-md">
            <p className="text-white">Create</p>
          </button>
        </div>

        {submittedData && (
          <pre className="bg-black text-green-400 p-4 rounded-md mt-6 text-sm overflow-auto">
            {submittedData}
          </pre>
        )}
      </div>
    </form>
  );
};
